import { base_colorAction } from 'ext-ui/style/colors'
import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { SidenavContentExpandedItemLinkProps } from 'src/components/SidenavContentExpandedItemLink/SidenavContentExpandedItemLink'
import SvgSidenavExpenseCategoriesActive from 'src/components/Svgs/SvgSidenavExpenseCategoriesActive'
import SvgSidenavExpenseCategoriesInactive from 'src/components/Svgs/SvgSidenavExpenseCategoriesInactive'
import SvgSidenavGeneral from 'src/components/Svgs/SvgSidenavGeneral'
import SvgSidenavIntegrations from 'src/components/Svgs/SvgSidenavIntegrations'
import SvgSidenavNotificationsActive from 'src/components/Svgs/SvgSidenavNotificationsActive'
import SvgSidenavNotificationsInactive from 'src/components/Svgs/SvgSidenavNotificationsInactive'
import SvgSidenavPersonalInfoActive from 'src/components/Svgs/SvgSidenavPersonalInfoActive'
import SvgSidenavPersonalInfoInactive from 'src/components/Svgs/SvgSidenavPersonalInfoInactive'
import SvgSidenavRulesAndWorkflowsActive from 'src/components/Svgs/SvgSidenavRulesAndWorkflowsActive'
import SvgSidenavRulesAndWorkflowsInactive from 'src/components/Svgs/SvgSidenavRulesAndWorkflowsInactive'
import SvgSidenavSecurityActive from 'src/components/Svgs/SvgSidenavSecurityActive'
import SvgSidenavSecurityInactive from 'src/components/Svgs/SvgSidenavSecurityInactive'
import { OrganizationRoles } from 'src/enums'
import { useExtendLocation } from 'src/hooks/locationHooks'
import { useTranslate } from 'src/localization/Trns'
import { UserFeature } from 'src/models/user.models'
import { AppLocation } from 'src/navigation/appLocations'
import { NavGroup, NavItem } from 'src/navigation/sidenav/types'
import {
  apiKeysUrl,
  automationRulesUrl,
  bankAccountsUrl,
  businessRulesUrl,
  cardFeedsUrl,
  departmentsUrl,
  expenseCategoriesAccountingUrl,
  expenseRequirementsUrl,
  manageSubscriptionUrl,
  organizationAccountingIntegrationsUrl,
  organizationInfoUrl,
  organizationProductivityIntegrationsUrl,
  organizationSsoUrl,
  orgSettingsReimbursementsUrl,
  profileInfoUrl,
  profileNotificationsUrl,
  profileSecurityUrl,
} from 'src/navigation/urls'
import { useQueryUserFeatures } from 'src/queries/auth.queries'
import { useQueryOrganizationFeatureFlags } from 'src/queries/organizations/featureflags.queries'
import { useQueryOwnUser } from 'src/queries/users.queries'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'
import { selectBrandFeaturesFromState } from 'src/redux/selectors/session.selectors'
import { canManageBillPay as resolveCanManageBillPay } from 'src/utils/billPayAccess'
import {
  isBillPayM1BankAccountsEnabled,
  isGoogleWorkspaceIntegrationsEnabled,
} from 'src/utils/featureFlags'

export interface SettingsNavItem {
  id: string
  label: string
  to?: string
  isActive: boolean
  activeSvg: React.ReactElement
  inactiveSvg: React.ReactElement
  expandOptions?: SidenavContentExpandedItemLinkProps[]
}

export interface UseSettingsNavItemsOptions {
  isSidenavOpen: boolean
  orgId?: string
}

export interface UseSettingsNavItemsResult {
  navGroups: NavGroup[]
}

export const useSettingsNavItems = (
  options: UseSettingsNavItemsOptions,
): UseSettingsNavItemsResult => {
  const { isSidenavOpen, orgId } = options
  const translate = useTranslate()

  const { data: currentUser } = useQueryOwnUser({})
  const actualOrgId = orgId || currentUser?.organizationId || ''

  const { data: orgFeatureFlags } = useQueryOrganizationFeatureFlags({
    orgId: actualOrgId,
  })

  const departmentsEnabled = orgFeatureFlags?.departmentsEnabled === true
  const showGuestRole = orgFeatureFlags?.guestRoleEnabled
  const isGuestUser =
    !!showGuestRole && currentUser?.organizationRole === OrganizationRoles.guest

  const {
    API_KEY_MANAGER: apiKeyManagerEnabled,
    VIEW_AUTOMATION_RULES_SECTION: canViewAutomationRules,
    SAAS_ENABLED: isSaasEnabled,
    SSO_ENABLED: orgSsoAllowed,
    BULK_CREDIT_CARD_ENROLLMENT: bulkCreditCardEnrollmentEnabled,
    VIEW_PAYABLES: viewPayablesEnabled,
    ORGANIZATION_USER_STATISTICS: orgUserStatisticsEnabled,
  } = useQueryUserFeatures([
    UserFeature.API_KEY_MANAGER,
    UserFeature.VIEW_AUTOMATION_RULES_SECTION,
    UserFeature.SAAS_ENABLED,
    UserFeature.SSO_ENABLED,
    UserFeature.BULK_CREDIT_CARD_ENROLLMENT,
    UserFeature.VIEW_PAYABLES,
    UserFeature.ORGANIZATION_USER_STATISTICS,
  ]).data || {}

  const { apiKeysEnabled, integrationsEnabled } = useSelector(
    selectBrandFeaturesFromState,
  )
  const remoteConfig = useSelector(selectFeatureFlaggerFromState)
  const cardFeedsEnabled = remoteConfig?.enable_card_feeds
  const showGoogleWorkspaceIntegrations =
    isGoogleWorkspaceIntegrationsEnabled(remoteConfig)
  const billPayBankAccountsEnabled = isBillPayM1BankAccountsEnabled(
    remoteConfig,
    actualOrgId,
  )
  // Owner/admin + full-visibility bookkeepers (EX-40473). Shared helper so the
  // nav item and the Bank Account page can never diverge — see src/utils/billPayAccess.
  const canManageBillPay = resolveCanManageBillPay({
    hasOrgAdminPermissions: currentUser?.hasOrgAdminPermissions,
    organizationRole: currentUser?.organizationRole,
    orgUserStatisticsEnabled,
  })

  const { location: appLocation } = useExtendLocation()

  const profileSettingsItems = useMemo(
    (): NavItem[] => [
      {
        id: 'settingsNavPersonalInfo',
        label: translate('Personal Info'),
        to: profileInfoUrl(),
        isActive: appLocation === AppLocation.ProfileInfo,
        activeSvg: <SvgSidenavPersonalInfoActive />,
        inactiveSvg: <SvgSidenavPersonalInfoInactive />,
      },
      {
        id: 'settingsNavNotifications',
        label: translate('Notifications'),
        to: profileNotificationsUrl(),
        isActive: appLocation === AppLocation.ProfileNotifications,
        activeSvg: <SvgSidenavNotificationsActive />,
        inactiveSvg: <SvgSidenavNotificationsInactive />,
      },
      {
        id: 'settingsNavSecurity',
        label: translate('Security'),
        to: profileSecurityUrl(),
        isActive: appLocation === AppLocation.ProfileSecurity,
        activeSvg: <SvgSidenavSecurityActive />,
        inactiveSvg: <SvgSidenavSecurityInactive />,
      },
    ],
    [appLocation, translate],
  )

  const generalItems: SidenavContentExpandedItemLinkProps[] = useMemo(
    () => [
      {
        id: 'settingsNavOrganizationInfo',
        label: translate('Organization Info'),
        to: organizationInfoUrl(actualOrgId),
        isActive:
          appLocation === AppLocation.OrganizationSettingsOrganizationInfo,
        isOpen: isSidenavOpen,
      },
      ...(orgSsoAllowed && currentUser?.hasOrgAdminPermissions
        ? [
            {
              id: 'settingsNavOrganizationSso',
              label: translate('SSO'),
              to: organizationSsoUrl(actualOrgId),
              isActive: appLocation === AppLocation.OrganizationSettingsSSO,
              isOpen: isSidenavOpen,
              feature: UserFeature.SSO_ENABLED,
            },
          ]
        : []),
      ...(currentUser?.hasOrgAdminPermissions && isSaasEnabled
        ? [
            {
              id: 'settingsManageSubscription',
              label: translate('Manage Plan'),
              to: manageSubscriptionUrl(actualOrgId),
              isActive:
                appLocation ===
                AppLocation.OrganizationSettingsManageSubscription,
              isOpen: isSidenavOpen,
            },
          ]
        : []),
      ...(billPayBankAccountsEnabled && canManageBillPay && viewPayablesEnabled
        ? [
            {
              id: 'settingsNavBankAccount',
              label: translate('Bank Account'),
              to: bankAccountsUrl(actualOrgId),
              isActive:
                appLocation === AppLocation.OrganizationSettingsBankAccounts,
              isOpen: isSidenavOpen,
            },
          ]
        : []),
      ...(currentUser?.hasOrgAdminPermissions && departmentsEnabled
        ? [
            {
              id: 'settingsNavTeams',
              label: translate('Teams'),
              to: departmentsUrl(actualOrgId),
              isActive:
                appLocation === AppLocation.OrganizationSettingsDepartments ||
                appLocation === AppLocation.DepartmentDetails,
              isOpen: isSidenavOpen,
            },
          ]
        : []),
      ...(!isGuestUser && apiKeysEnabled && apiKeyManagerEnabled
        ? [
            {
              id: 'settingsNavApiKeys',
              label: translate('API Key'),
              to: apiKeysUrl(actualOrgId),
              isActive: appLocation === AppLocation.OrganizationSettingsAPIKeys,
              isOpen: isSidenavOpen,
              feature: UserFeature.API_KEY,
            } satisfies SidenavContentExpandedItemLinkProps,
          ]
        : []),
    ],
    [
      isSaasEnabled,
      actualOrgId,
      appLocation,
      currentUser?.hasOrgAdminPermissions,
      departmentsEnabled,
      apiKeysEnabled,
      apiKeyManagerEnabled,
      isSidenavOpen,
      isGuestUser,
      translate,
      orgSsoAllowed,
      billPayBankAccountsEnabled,
      canManageBillPay,
      viewPayablesEnabled,
    ],
  )

  const integrationsItems: SidenavContentExpandedItemLinkProps[] = useMemo(
    () => [
      ...(integrationsEnabled
        ? [
            {
              id: 'settingsNavAccountingErp',
              label: translate('Accounting'),
              to: organizationAccountingIntegrationsUrl(actualOrgId),
              isActive:
                appLocation === AppLocation.OrganizationSettingsIntegrations ||
                appLocation ===
                  AppLocation.OrganizationSettingsIntegrationsAccounting,
              isOpen: isSidenavOpen,
            },
          ]
        : []),
      ...(showGoogleWorkspaceIntegrations
        ? [
            {
              id: 'settingsNavProductivity',
              label: translate('Productivity'),
              to: organizationProductivityIntegrationsUrl(actualOrgId),
              isActive:
                appLocation ===
                AppLocation.OrganizationSettingsIntegrationsProductivity,
              isOpen: isSidenavOpen,
            },
          ]
        : []),
      ...(bulkCreditCardEnrollmentEnabled && cardFeedsEnabled
        ? [
            {
              id: 'settingsNavCardFeeds',
              label: translate('Card Feeds'),
              to: cardFeedsUrl(),
              isActive:
                appLocation === AppLocation.OrganizationSettingsCardFeeds ||
                appLocation ===
                  AppLocation.OrganizationSettingsCardFeedCreate ||
                appLocation === AppLocation.OrganizationSettingsCardFeedDetails,
              isOpen: isSidenavOpen,
            } satisfies SidenavContentExpandedItemLinkProps,
          ]
        : []),
    ],
    [
      actualOrgId,
      appLocation,
      bulkCreditCardEnrollmentEnabled,
      cardFeedsEnabled,
      integrationsEnabled,
      isSidenavOpen,
      showGoogleWorkspaceIntegrations,
      translate,
    ],
  )

  const rulesAndWorkflowsItems: SidenavContentExpandedItemLinkProps[] = useMemo(
    () => [
      ...(canViewAutomationRules
        ? [
            {
              id: 'settingsNavAutomation',
              label: translate('Automation'),
              to: automationRulesUrl(actualOrgId),
              isActive: [
                AppLocation.OrganizationSettingsAutomationRules,
                AppLocation.CreateAutomationRule,
                AppLocation.OrganizationSettingsReceiptRules,
                AppLocation.OrganizationSettingsExpenseSettings,
              ].includes(appLocation),
              isOpen: isSidenavOpen,
              feature: UserFeature.EXPENSE_CATEGORY_RULES,
            },
          ]
        : []),
      {
        id: 'settingsNavApprovers',
        label: translate('Approvers'),
        to: businessRulesUrl(actualOrgId),
        isActive: appLocation === AppLocation.OrganizationSettingsBusinessRules,
        isOpen: isSidenavOpen,
        feature: UserFeature.BUSINESS_RULES,
      },
      {
        id: 'settingsNavExpenseRequirements',
        label: translate('Expense Requirements'),
        to: expenseRequirementsUrl(actualOrgId),
        isActive:
          appLocation === AppLocation.OrganizationSettingsExpenseRequirements ||
          appLocation ===
            AppLocation.OrganizationSettingsExpenseRequirementsReceiptRules,
        isOpen: isSidenavOpen,
      },
      {
        id: 'settingsReimbursements',
        label: translate('Reimbursements'),
        to: orgSettingsReimbursementsUrl(actualOrgId),
        isActive:
          appLocation === AppLocation.OrganizationSettingsReimbursements,
        isOpen: isSidenavOpen,
        feature: UserFeature.MANAGER_ASSIGNMENT,
      },
    ],
    [
      actualOrgId,
      canViewAutomationRules,
      appLocation,
      isSidenavOpen,
      translate,
    ],
  )

  const organizationSettingsItems = useMemo(
    (): NavItem[] => [
      {
        id: 'settingsNavGeneral',
        label: translate('General'),
        isActive: generalItems.some((item) => item.isActive),
        activeSvg: <SvgSidenavGeneral stroke={base_colorAction} />,
        inactiveSvg: <SvgSidenavGeneral />,
        expandOptions: generalItems,
      },
      ...(integrationsItems.length > 0
        ? [
            {
              id: 'settingsNavIntegrations',
              label: translate('Integrations'),
              isActive: integrationsItems.some((item) => item.isActive),
              activeSvg: <SvgSidenavIntegrations stroke={base_colorAction} />,
              inactiveSvg: <SvgSidenavIntegrations />,
              expandOptions: integrationsItems,
            },
          ]
        : []),
      {
        id: 'settingsNavExpenseCategories',
        label: translate('Expense Categories'),
        to: expenseCategoriesAccountingUrl(actualOrgId),
        isActive:
          [
            AppLocation.OrganizationSettingsExpenseCategories,
            AppLocation.OrganizationSettingsExpenseCategoryLabels,
          ].includes(appLocation) ||
          appLocation ===
            AppLocation.OrganizationSettingsExpenseCategoriesAdditionalFields,
        activeSvg: <SvgSidenavExpenseCategoriesActive />,
        inactiveSvg: <SvgSidenavExpenseCategoriesInactive />,
      },
      {
        id: 'settingsNavRulesAndWorkflows',
        label: translate('Rules & Workflows'),
        isActive: rulesAndWorkflowsItems.some((item) => item.isActive),
        activeSvg: <SvgSidenavRulesAndWorkflowsActive />,
        inactiveSvg: <SvgSidenavRulesAndWorkflowsInactive />,
        expandOptions: rulesAndWorkflowsItems,
      },
    ],
    [
      appLocation,
      generalItems,
      integrationsItems,
      rulesAndWorkflowsItems,
      actualOrgId,
      translate,
    ],
  )

  const navGroups = useMemo(
    (): NavGroup[] => [
      // Profile Settings
      {
        items: profileSettingsItems,
        header: translate('Profile Settings'),
        condition: true,
      },
      // Organization Settings
      {
        items: organizationSettingsItems,
        header: translate('Organization Settings'),
        showDivider: true,
        condition: currentUser?.hasOrgBookkeeperPermissions,
      },
    ],
    [profileSettingsItems, organizationSettingsItems, translate],
  )

  return {
    navGroups,
  }
}
